import Check from '@mui/icons-material/Check'
import Close from '@mui/icons-material/Close'
import { Chip, CircularProgress } from '@mui/material'
import Popover from '@mui/material/Popover'
import debounce from 'lodash/debounce'
import { Suspense, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { toast } from 'react-toastify'
import caretPurpleDownIcon from 'src/assets/caret_purple_down.png'
import { getPriceStringBusinessPlan } from 'src/business/helpers/helperFunctions'
import {
  CustomInput,
  CustomSelect,
  CustomTextArea,
  selectTypes,
} from 'src/consumer/components/helperComponents'
import { CustomAsyncCreatablePaginate } from 'src/consumer/components/helperComponents/dynamic'
import { StyledLabel } from 'src/consumer/components/helperComponentsStyles'
import { CLINIC_TYPES, timezones_list } from 'src/consumer/constants'
import { ProfileContext } from 'src/consumer/context/ProfileContext'
import { UnsecureSetupContext } from 'src/consumer/context/UnsecureSetupContext'
import { StyledClinicBrandPhoneNumberInput } from 'src/mentor/components/MentorProfilePage/MentorProfilePageStyles'
import { loadCities } from 'src/mentor/components/MentorPublicPage/MentorPublicPage'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'
import { getMPDataWithoutParams, getMPDataWithParams } from 'src/mentor/services'
import {
  BusinessOnboardingBody,
  BusinessOnboardingBodyDescription,
  BusinessOnboardingBodyHeading,
  BusinessOnboardingBodyPlanSelectorArrow,
  BusinessOnboardingBodyPlanSelectorText,
  BusinessOnboardingInput,
  BusinessOnboardingInputContainer,
  BusinessOnboardingSingleSelectContainer,
  BusinessOnboardingSingleSelectOption,
  BusinessOnboardingSingleSelectOptionsContainer,
  BusinessOnboardingSubmitButton,
  PeopleCheckIconBox,
  StyledClinicBrandStaffInputComponent,
  StyledOnboardingAsyncCreatableSelect,
  StyledOnboardingSelect,
  StyledOnboardingTextArea,
  StyledSectionHeading,
  StyledSectionSubHeading,
} from './ClinicOnboardingStyles'
import {
  getAllContactsOfAClinicBrand,
  isClinicStaffAlreadyAdded
} from './locationOnboardingUtils'

export const BusinessOnboardingInputComponent = ({ type, title, enteredValue, setterFunction }) => {
  return (
    <>
      <StyledLabel>{title}</StyledLabel>
      <BusinessOnboardingInput
        type={type}
        value={enteredValue}
        onChange={(event) => setterFunction(event.target.value)}
      />
    </>
  )
}
export const ClinicOnboardingInputComponentWithHTMLProps = ({ title, ...props }) => {
  return (
    <>
      <StyledLabel>{title}</StyledLabel>
      <BusinessOnboardingInput {...props} />
    </>
  )
}

export const BusinessOnboardingSingleSelect = ({
  idForSelection,
  title,
  options,
  selectedOptionId,
  setterFunction,
  showPrices,
  currency,
}) => {
  const [anchorForSingleSelectPopover, setAnchorForSingleSelectPopover] = useState()

  const handleOpenClick = (event) => {
    setAnchorForSingleSelectPopover(event.currentTarget)
  }
  const handleCloseClick = () => {
    setAnchorForSingleSelectPopover(null)
  }

  const selectionOpen = Boolean(anchorForSingleSelectPopover)
  const idForPopover = selectionOpen ? idForSelection : undefined

  return (
    <>
      <div>{title}</div>
      <BusinessOnboardingSingleSelectContainer onClick={(event) => handleOpenClick(event)}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <BusinessOnboardingBodyPlanSelectorText>
            {options?.find((item) => item?.id === selectedOptionId)?.title}
          </BusinessOnboardingBodyPlanSelectorText>
          {showPrices && (
            <div>
              <BusinessOnboardingBodyPlanSelectorText bold gray>
                {getPriceStringBusinessPlan(
                  currency,
                  options?.find((item) => item?.id === selectedOptionId)?.pricePEPM,
                )}
              </BusinessOnboardingBodyPlanSelectorText>
              <BusinessOnboardingBodyPlanSelectorText bold green>
                {options?.find((item) => item?.id === selectedOptionId)?.bestValue && 'Best Value'}
              </BusinessOnboardingBodyPlanSelectorText>
            </div>
          )}
        </div>
        <BusinessOnboardingBodyPlanSelectorArrow
          menuOpen={anchorForSingleSelectPopover}
          src={caretPurpleDownIcon}
        />
      </BusinessOnboardingSingleSelectContainer>
      <Popover
        id={idForPopover}
        open={selectionOpen}
        anchorEl={anchorForSingleSelectPopover}
        onClose={handleCloseClick}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <BusinessOnboardingSingleSelectOptionsContainer considerPadding={showPrices}>
          {options?.map((item) => (
            <BusinessOnboardingSingleSelectOption
              key={item?.id}
              selected={selectedOptionId === item?.id}
              onClick={() => {
                setterFunction(item?.id)
                handleCloseClick()
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <BusinessOnboardingBodyPlanSelectorText>
                  {item?.title}
                </BusinessOnboardingBodyPlanSelectorText>
                {showPrices && (
                  <div>
                    <BusinessOnboardingBodyPlanSelectorText bold gray>
                      {getPriceStringBusinessPlan(currency, item?.pricePEPM)}
                    </BusinessOnboardingBodyPlanSelectorText>
                    <BusinessOnboardingBodyPlanSelectorText bold green>
                      {item?.bestValue && 'Best Value'}
                    </BusinessOnboardingBodyPlanSelectorText>
                  </div>
                )}
              </div>
            </BusinessOnboardingSingleSelectOption>
          ))}
        </BusinessOnboardingSingleSelectOptionsContainer>
      </Popover>
    </>
  )
}

const formComponentContainerClasses =
  'd-flex flex-column justify-content-start align-items-stretch gap-1 w-100'

export const ClinicLocationOnboardingComponent = ({
  clinicLocationData,
  setClinicLocationData,
  saveClinicLocation = () => {
    console.log('saveClinicLocation no method set')
  },
  customHeading,
  savingClinicLocation,
  clinicParentBrandData,
}) => {
  const { unSecureSetupData } = useContext(UnsecureSetupContext)
  const [slugValidationLoading, setSlugValidationLoading] = useState(false)
  const [slugValid, setSlugValid] = useState(false)
  // const [suggestedSlug, setSuggestedSlug] = useState('')
  const [initialSlugValue, setInitialSlugValue] = useState('')
  const [slugInvalidFormat, setSlugInvalidFormat] = useState(false)

  const slugInputRef = useRef()

  const showTempSlug = !clinicLocationData?.slug

  async function validateSlug(currentSlug, initialSlugValue) {
    setSlugValid(false)
    setSlugInvalidFormat(false)
    setSlugValidationLoading(true)
    if (Boolean(currentSlug)) {
      const pattern = /^[a-z\-]+$/ // match only a-z and hyphen
      const matchesPattern = pattern.test(currentSlug)
      if (currentSlug === initialSlugValue) {
        setSlugValid(true)
      } else if (matchesPattern) {
        const response = await getMPDataWithoutParams(
          `/api/secure/cp/clinic-location/slug/check-availability?slug=${currentSlug}`,
        )
        if (response?.status === 200) {
          if (response?.data) {
            setSlugValid(true)
          } else {
            setSlugValid(false)
          }
        } else {
          toast.error('Error checking slug availability')
        }
      } else {
        setSlugInvalidFormat(true)
        setSlugValid(false)
      }
    } else {
      setSlugInvalidFormat(true)
      setSlugValid(false)
    }
    setSlugValidationLoading(false)
  }

  const slugValidationFunc = useCallback(
    debounce(async (slug) => {
      await validateSlug(slug, initialSlugValue)
    }, 500),
    [initialSlugValue],
  )

  async function getSuggestedSlug({ formData }) {
    const response = await getMPDataWithParams('/api/secure/cp/clinic/slug/suggest', {
      title: formData?.title,
    })
    if (response?.status === 200) {
      setClinicLocationData((prev) => ({
        ...prev,
        tempSlug: response?.data,
      }))
      slugValidationFunc(response?.data)
    }
  }

  function slugFocusHandling() {
    if (!clinicLocationData?.slug && !!clinicLocationData?.tempSlug) {
      setClinicLocationData((prev) => ({
        ...prev,
        slug: prev?.tempSlug || '',
      }))
    }
  }

  const debSlugSuggest = useCallback(
    debounce(async ({ formData }) => {
      await getSuggestedSlug({ formData })
    }, 500),
    [],
  )

  useEffect(() => {
    if (clinicLocationData?.id) getSuggestedSlug(setClinicLocationData?.brandName)
  }, [])

  useEffect(() => {
    if (!clinicLocationData?.slug) {
      debSlugSuggest({ formData: clinicLocationData })
    }
  }, [clinicLocationData?.title])

  // async function getSuggestedSlug(title) {
  //   const response = await getMPDataWithParams('/api/secure/cp/clinic/slug/suggest', {
  //     title,
  //   })
  //   if (response?.status === 200) {
  //     setSuggestedSlug(response?.data)
  //   }
  // }

  // const debounceGetSuggestedSlug = useCallback(
  //   debounce(async (title) => {
  //     await getSuggestedSlug(title)
  //   }, 500),
  //   [],
  // )

  // useEffect(() => {
  //   if (clinicLocationData?.id) getSuggestedSlug(clinicLocationData?.brandName)
  // }, [])
  useEffect(() => {
    if (initialSlugValue) {
    } else {
      setInitialSlugValue(clinicLocationData?.slug)
      setSlugValid(clinicLocationData?.slug || clinicLocationData?.tempSlug)
    }
  }, [clinicLocationData])

  return (
    <BusinessOnboardingBody useMaxWidth maxWidth={'576px'}>
      <BusinessOnboardingBodyHeading>
        {customHeading || 'Clinic Location Details'}
      </BusinessOnboardingBodyHeading>
      {!Boolean(clinicLocationData?.id) && (
        <BusinessOnboardingBodyDescription>
          <b>
            (Adding New Location for{' '}
            {clinicParentBrandData?.brandName || clinicParentBrandData?.title})
          </b>
        </BusinessOnboardingBodyDescription>
      )}
      <div className={formComponentContainerClasses} style={{ marginBottom: '20px' }}>
        <BusinessOnboardingInputComponent
          type="text"
          title="Clinic Location Name"
          enteredValue={
            clinicLocationData?.firstName || clinicLocationData?.name || clinicLocationData?.title
          }
          setterFunction={(id) => {
            setClinicLocationData((prev) => ({
              ...prev,
              firstName: id,
              name: id,
              title: id,
            }))
            // debounceGetSuggestedSlug(id)
          }}
        />
      </div>
      {/* <div className={formComponentContainerClasses} style={{ marginBottom: '20px' }}>
        <BusinessOnboardingInputComponent
          type="email"
          title="Email"
          enteredValue={clinicLocationData?.email}
          setterFunction={(data) => setClinicLocationData((prev) => ({ ...prev, email: data }))}
        />
      </div> */}
      <div className={formComponentContainerClasses} style={{ marginBottom: '20px' }}>
        <StyledLabel>URL for this location: </StyledLabel>
        <BusinessOnboardingInputContainer
          className="align-items-center hide-scrollbar"
          onClick={() => {
            slugInputRef.current.focus()
          }}
          style={{ textDecoration: 'underline', height: 'unset', overflowX: 'auto' }}
        >
          <span className="flex-shrink-0">
            https://aumhum.com/clinic/{clinicParentBrandData?.slug}/
          </span>
          <BusinessOnboardingInput
            style={{
              border: 'none',
              width: 'unset',
              paddingLeft: 0,
              flexShrink: 0,
            }}
            ref={slugInputRef}
            id={showTempSlug ? 'tempSlug' : 'slug'}
            value={!showTempSlug ? clinicLocationData?.slug : clinicLocationData?.tempSlug}
            onFocus={slugFocusHandling}
            type="text"
            onChange={(event) => {
              setClinicLocationData((prev) => ({ ...prev, slug: event.target.value }))
              slugValidationFunc(event.target.value)
            }}
          />
        </BusinessOnboardingInputContainer>
        {slugValidationLoading && <div style={{ marginLeft: '5px' }}>Checking availability...</div>}
        {/* {slugValid
          ? !slugValidationLoading && (
              <div style={{ color: 'green', marginLeft: '5px' }}>Looks good!</div>
            )
          : !slugValidationLoading && (
              <div style={{ color: 'red', marginLeft: '5px' }}>
                Not available{' '}
                <span style={{ color: 'rgba(44, 56, 74, 0.95)' }}>
                  {suggestedSlug && (
                    <>
                      Suggested:{' '}
                      <span
                        style={{ textDecoration: 'underline', cursor: 'pointer' }}
                        onClick={() => {
                          setClinicLocationData((prev) => ({
                            ...prev,
                            slug: suggestedSlug,
                          }))
                          setSlugValid(true)
                        }}
                      >
                        {suggestedSlug}
                      </span>
                    </>
                  )}
                </span>
              </div>
            )} */}
        {clinicLocationData?.slug && slugInvalidFormat && (
          <div style={{ color: 'red', marginLeft: '5px' }}>
            Please use lower case letters and hyphens only
          </div>
        )}
        {!slugInvalidFormat &&
          clinicLocationData?.slug &&
          (slugValid
            ? !slugValidationLoading && (
                <div style={{ color: 'green', marginLeft: '5px' }}>Looks good!</div>
              )
            : !slugValidationLoading && (
                <div style={{ color: 'red', marginLeft: '5px' }}>Not available, try another</div>
              ))}
      </div>
      <div className={formComponentContainerClasses} style={{ marginBottom: '20px' }}>
        <div>Telephone (Branch Contact Number, Not your personal Number)</div>
        <CustomInput
          inputAndMessageContainer={StyledClinicBrandStaffInputComponent}
          component={StyledClinicBrandPhoneNumberInput}
          value={(clinicLocationData?.mobile || '')?.replace('+', '')}
          country="in"
          showDropDown={false}
          onChange={(data) => setClinicLocationData((prev) => ({ ...prev, mobile: `+${data}` }))}
          invalid={
            !clinicLocationData?.mobile || !isValidPhoneNumber(`${clinicLocationData?.mobile}`)
          }
          invalidMessage={
            !clinicLocationData?.mobile
              ? 'Mobile cannot be blank.'
              : !isValidPhoneNumber(`${clinicLocationData?.mobile}`)
              ? 'Ensure your phone number has a country code and is 10 digits long.'
              : 'Looks like this phone is already the list. Please use a different one.'
          }
        />
        {/* <StyledPhoneNumberInput
          defaultCountry="IN"
          countryCallingCodeEditable={false}
          withCountryCallingCode={true}
          id="mobile"
          value={clinicLocationData?.mobile}
          
          placeholder="Enter the Mobile/Telephone..."
        /> */}
      </div>
      <div style={{ marginBottom: '20px' }}>
        <CustomSelect
          value={clinicLocationData?.cloudClinic ? CLINIC_TYPES[1] : CLINIC_TYPES[0]}
          options={CLINIC_TYPES}
          getOptionValue={(option) => option?.id}
          getOptionLabel={(option) => option?.title}
          menuPlacement="auto"
          onChangeEvent={(event) => {
            if (event.target.value.id === CLINIC_TYPES[1]?.id) {
              setClinicLocationData((prev) => ({ ...prev, cloudClinic: true }))
            } else {
              setClinicLocationData((prev) => ({ ...prev, cloudClinic: false }))
            }
          }}
          selectType={selectTypes.object}
          customComponent={StyledOnboardingSelect}
          label="Clinic Type"
        />
      </div>

      {!clinicLocationData?.cloudClinic && (
        <>
          <StyledSectionHeading>Address</StyledSectionHeading>
          <StyledSectionSubHeading>Tell us where your clinic is located</StyledSectionSubHeading>
          <div className={formComponentContainerClasses} style={{ marginBottom: '20px' }}>
            <CustomTextArea
              label="Address"
              customTextAreaComponent={StyledOnboardingTextArea}
              minHeight={80}
              value={clinicLocationData?.location?.address}
              onChange={(e) =>
                setClinicLocationData((prevData) => ({
                  ...prevData,
                  location: { ...prevData.location, address: e.target.value },
                }))
              }
            />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <CustomSelect
              value={clinicLocationData?.location?.countryCode}
              options={
                unSecureSetupData?.metaCountries?.sort((c1, c2) => {
                  if (c1.title < c2.title) {
                    return -1
                  }
                  if (c1.title > c2.title) {
                    return 1
                  }
                  return 0
                }) || []
              }
              getOptionValue={(option) => option?.id}
              getOptionLabel={(option) => option?.title}
              menuPlacement="auto"
              selectType={selectTypes.string}
              onChangeEvent={(e) =>
                setClinicLocationData((prevData) => ({
                  ...prevData,
                  countryCode: { ...prevData.location, countryCode: e.target.value },
                }))
              }
              customComponent={StyledOnboardingSelect}
              label={'Country'}
            />
          </div>
          <div className={formComponentContainerClasses} style={{ marginBottom: '20px' }}>
            <Suspense fallback={<CircularProgress size={20} />}>
              <CustomAsyncCreatablePaginate
                id="city"
                label="City"
              cacheUniqs={[clinicLocationData?.location?.countryCode]}
              value={
                clinicLocationData?.location?.city
                  ? {
                      id: clinicLocationData?.location?.city,
                      title: clinicLocationData?.location?.city,
                    }
                  : null
              }
              loadOptions={loadCities}
              isClearable
              AsyncCreatableSelect={StyledOnboardingAsyncCreatableSelect}
              onChange={(val) => {
                setClinicLocationData((prev) => {
                  return {
                    ...prev,
                    location: {
                      ...prev.location,
                      city: val?.title,
                    },
                  }
                })
              }}
              onCreateOption={(cOptn) => {
                setClinicLocationData((prev) => {
                  return {
                    ...prev,
                    location: {
                      ...prev.location,
                      city: cOptn,
                    },
                  }
                })
              }}
              getOptionValue={(option) => option?.id}
              getOptionLabel={(option) => option.title}
              // inputvalue={qualificationInput}
              // onInputChange={handleInputChange}
              placeholder="Select city..."
              debounceTimeout={500}
              getNewOptionData={(inputValue, optionLabel) => ({
                id: inputValue,
                title: optionLabel,
              })}
              formatCreateLabel={(value) => `Press enter to select: ${value}`}
              selectType={selectTypes.string}
              noOptionsMessage={() => 'Enter your city.'}
              additional={{
                page: 0,
                size: 100,
                countryCode: clinicLocationData?.location?.countryCode,
              }}
              styles={{
                option: (provided, state) => {
                  if (state.data.__isNew__) {
                    return { ...provided, color: '#6b50aa' }
                  } else {
                    return provided
                  }
                },
              }}
              />
            </Suspense>
            {/* <BusinessOnboardingInputComponent
              type="text"
              title="City"
              enteredValue={clinicLocationData?.location?.city}
              setterFunction={(data) =>
                setClinicLocationData((prevData) => ({
                  ...prevData,
                  location: { ...prevData.location, city: data },
                }))
              }
            /> */}
          </div>
          <div style={{ marginBottom: '20px' }}>
            <CustomSelect
              value={{
                gmtvalue: decodeURIComponent(clinicLocationData?.timeZone?.offset),
                name: clinicLocationData?.timeZone?.title,
              }}
              options={timezones_list}
              getOptionValue={(option) => option?.gmtvalue}
              getOptionLabel={(option) => option?.name}
              menuPlacement="auto"
              onChange={(value) =>
                setClinicLocationData((prev) => ({
                  ...prev,
                  timeZone: {
                    ...prev.timeZone,
                    offset: encodeURIComponent(value.gmtvalue),
                    title: value.name,
                  },
                }))
              }
              customComponent={StyledOnboardingSelect}
              label="Time Zone"
            />
          </div>
        </>
      )}

      <BusinessOnboardingSubmitButton
        style={{ marginBottom: '20px' }}
        onClick={saveClinicLocation}
        disabled={
          !(
            (clinicLocationData?.name ||
              clinicLocationData?.firstName ||
              clinicLocationData?.title) &&
            (clinicLocationData?.email || clinicLocationData?.mobile) &&
            slugValid &&
            (!clinicLocationData?.cloudClinic
              ? clinicLocationData?.location?.countryCode &&
                clinicLocationData?.location?.city &&
                !!clinicLocationData?.timeZone?.offset
              : true) &&
            clinicParentBrandData?.id
          )
        }
      >
        <span>{clinicParentBrandData?.id ? 'Continue' : 'Invalid Brand'}</span>
        {savingClinicLocation && <Spinner animation="border" variant="light" size="sm" />}
      </BusinessOnboardingSubmitButton>
    </BusinessOnboardingBody>
  )
}

export const ClinicLocationStaffComponent = ({
  staffObject,
  setStaffObject,
  saveStaffFunc = () => {
    console.log('saveStaffFunc no method set')
  },
  customHeading,
  savingStaff,
  peopleInThisBrandData,
  clinicParentBrandData,
  mentorsInThisBrandData,
  inOnboarding,
}) => {
  // const [clinicStaff, setClinicStaff] = useState()
  // const [reviewStaffMembers, setReviewStaffMembers] = useState(false)
  // const [staffAccordionState, setStaffAccordionState] = useState('doctors')
  // const [staffValidation, setStaffValidation] = useState()

  // function getFirstFoundSlot(weeklyHourMap) {
  //   const foundEntry = Object.entries(weeklyHourMap).find(([key, value], idx) => {
  //     return !!(
  //       value?.slotList?.[0] &&
  //       value?.slotList?.[0]?.startTime &&
  //       value?.slotList?.[0]?.endTime
  //     )
  //   })
  //   return foundEntry
  //     ? foundEntry?.[1]?.slotList?.[0]
  //     : { startTime: '09:00:00', endTime: '17:00:00' }
  // }

  // function checkMobile(mobile) {
  //   if (!!mobile) {
  //     const firstLetter = mobile?.[0]
  //     return firstLetter === '+' ? mobile : `+${mobile}`
  //   }
  //   return null
  // }

  // const days = [
  //   { id: 'monday', label: 'Monday' },
  //   { id: 'tuesday', label: 'Tuesday' },
  //   { id: 'wednesday', label: 'Wednesday' },
  //   { id: 'thursday', label: 'Thursday' },
  //   { id: 'friday', label: 'Friday' },
  //   { id: 'saturday', label: 'Saturday' },
  //   { id: 'sunday', label: 'Sunday' },
  // ]

  // function showStaffDeleteButton(id, index) {
  //   if (id === 'management') {
  //     if (index === 0) {
  //       return false
  //     }
  //   }
  //   return true
  // }

  // staff related funcs
  // const handleAddStaff = (newStaff) => {
  //   const { type, ...rest } = newStaff
  //   setClinicStaff((prevData) => ({
  //     ...prevData,
  //     [type]: [...(prevData?.[type] || []), rest],
  //   }))
  // }

  // const handleRemoveStaff = (type, index) => {
  //   setClinicStaff((prevData) => ({
  //     ...prevData,
  //     [type]: prevData[type].filter((_, i) => i !== index),
  //   }))
  // }

  // const handleReviewStaff = () => {
  //   setReviewStaffMembers(true)
  // }
  const { profileData: currentUserProfileData } = useContext(ProfileContext)

  const [userContactDataOfAllPeopleInThisBrand, setUserContactDataOfAllPeopleInThisBrand] =
    useState({ doctors: [], therapists: [], assistants: [] })

  function dataSetFunction() {
    const toReturnObject = getAllContactsOfAClinicBrand({
      clinicBrandData: clinicParentBrandData,
      mentorsInThisBrandData,
      peopleInThisBrandData,
    })

    console.log({ toReturnObject })
    setUserContactDataOfAllPeopleInThisBrand(toReturnObject)
  }

  function clickStaffMember({ userContactData, staffKey }) {
    if (
      isClinicStaffAlreadyAdded({
        staffKey: staffKey,
        staffObject: staffObject,
        userContactData: userContactData,
      })
    ) {
      const copyOfStaffObject = { ...staffObject }
      const updatedDoctors = copyOfStaffObject?.doctors?.filter((item) =>
        filterFunc(item, userContactData),
      )
      setStaffObject((prevState) => ({ ...prevState, doctors: updatedDoctors }))
    } else {
      const updatedDoctors = [...staffObject.doctors, userContactData]
      setStaffObject((prevState) => ({ ...prevState, doctors: updatedDoctors }))
    }
  }

  useEffect(() => {
    dataSetFunction()
  }, [])

  function filterFunc(item, userContactData) {
    let toReturn = true
    if (userContactData?.userId) {
      toReturn = item?.userId !== userContactData?.userId
    } else if (userContactData?.email) {
      toReturn = item?.email !== userContactData?.email
    } else if (userContactData?.mobile) {
      toReturn = item?.mobile !== userContactData?.mobile
    }
    return toReturn
  }

  function renderStaff({ userContactData, idx, staffKey }) {
    const isYou = userContactData?.userId === currentUserProfileData?.userId

    return (
      <PeopleAddorRemoveComponent
        name={`${userContactData?.title}`}
        isLoggedInUser={isYou}
        email={userContactData?.email}
        mobile={userContactData?.mobile}
        image={userContactData?.profilePicId}
        alreadyAdded={isClinicStaffAlreadyAdded({
          staffKey: staffKey,
          staffObject: staffObject,
          userContactData: userContactData,
        })}
        clickFunction={() =>
          clickStaffMember({
            staffKey: staffKey,
            userContactData,
          })
        }
        key={idx}
      />
    )
  }

  return (
    <BusinessOnboardingBody useMaxWidth maxWidth={'576px'}>
      <BusinessOnboardingBodyHeading>Choose Clinic Staff</BusinessOnboardingBodyHeading>
      <BusinessOnboardingBodyDescription>
        Choose available staff from your brand to add to this location.
      </BusinessOnboardingBodyDescription>

      {/* doctors */}
      <div>
        <div style={{ fontWeight: '500', fontSize: '22px', marginBottom: '10px' }}>Doctors</div>
        <div>
          {userContactDataOfAllPeopleInThisBrand?.doctors?.map((userContactData, idx) =>
            renderStaff({ userContactData, idx, staffKey: 'doctors' }),
          )}
        </div>
      </div>

      {/* therapists */}
      <div>
        <div style={{ fontWeight: '500', fontSize: '22px', marginBottom: '10px' }}>Therapists</div>
        <div>
          {userContactDataOfAllPeopleInThisBrand?.therapists?.map((userContactData, idx) => {
            return renderStaff({ userContactData, idx, staffKey: 'therapists' })
          })}
        </div>
      </div>

      {/* clinic assistants */}
      <div>
        <div style={{ fontWeight: '500', fontSize: '22px', marginBottom: '10px' }}>
          Clinic Assistants
        </div>
        <div>
          {userContactDataOfAllPeopleInThisBrand?.assistants?.map((userContactData, idx) => {
            return renderStaff({ userContactData, idx, staffKey: 'assistants' })
          })}
        </div>
      </div>

      <BusinessOnboardingSubmitButton
        style={{ margin: '20px 0 40px' }}
        onClick={saveStaffFunc}
        disabled={savingStaff}
      >
        <span>{clinicParentBrandData?.id ? 'Continue' : 'Invalid Brand'}</span>
        {savingStaff && <Spinner animation="border" variant="light" size="sm" />}
      </BusinessOnboardingSubmitButton>
    </BusinessOnboardingBody>
  )
}

const PeopleAddorRemoveComponent = ({
  name,
  email,
  mobile,
  image,
  alreadyAdded = false,
  clickFunction,
  isLoggedInUser = false,
}) => {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        margin: '10px 0',
        alignItems: 'center',
      }}
    >
      <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
        <PeopleCheckIconBox alreadyAdded={alreadyAdded}>
          {alreadyAdded ? (
            <Check fontSize="25px" sx={{ color: 'white' }} />
          ) : (
            <Close fontSize="25px" sx={{ color: 'white' }} />
          )}
        </PeopleCheckIconBox>
        <img
          src={getImageLink(image)}
          style={{ height: '50px', width: '50px', borderRadius: '50%' }}
          alt="person"
        />
        <div className="d-flex align-items-center gap-1">
          <b>{name || email || mobile}</b>
          {isLoggedInUser && <Chip label="You" size="small" color="emrPrimary" />}
        </div>
      </div>
      <div>
        <button
          onClick={clickFunction}
          style={{
            background: alreadyAdded ? 'orange' : 'green',
            color: 'white',
            borderRadius: '8px',
            padding: '4px 8px',
          }}
        >
          {alreadyAdded ? 'Remove' : 'Add'}
        </button>
      </div>
    </div>
  )
}

/* {clinicStaffTypesWithJsonFields?.map(({ id, title, singleTitle }) => {
        return (
          <Accordion
            expanded={staffAccordionState === id || reviewStaffMembers}
            onChange={() => setStaffAccordionState(id)}
            key={id}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <StyledSectionHeading
                className="d-flex align-items-center gap-2"
                error={!!staffValidation?.[id]}
              >
                {title}
                <Chip
                  color={!!staffValidation?.[id] ? 'error' : 'default'}
                  label={
                    !clinicStaff?.[id] || clinicStaff?.[id]?.length === 0
                      ? 'None'
                      : clinicStaff?.[id]?.length
                  }
                  sx={{
                    height: '16px',
                    '> .MuiChip-label': { paddingLeft: '8px', paddingRight: '8px' },
                  }}
                />
              </StyledSectionHeading>
            </AccordionSummary>
            <AccordionDetails>
              <>
                {clinicStaff?.[id]?.length !== 0 && !!clinicStaff?.[id] && (
                  <div className="d-flex flex-column gap-3">
                    {clinicStaff?.[id]?.map((staff, index) => (
                      <StyledClinicStaffView className="d-flex justify-content-between" key={index}>
                        <div className="d-flex flex-column justify-content-stretch align-items-start gap-2">
                          {!!staff?.title && <span>Name: {staff.title}</span>}
                          {!!staff.email && <span>Email: {staff.email}</span>}
                          {!!staff.mobile && <span>Mobile: {staff.mobile}</span>}
                        </div>
                        {showStaffDeleteButton(id, index) && (
                          <div>
                            <IconButton onClick={() => handleRemoveStaff(id, index)}>
                              <Delete />
                            </IconButton>
                          </div>
                        )}
                      </StyledClinicStaffView>
                    ))}
                  </div>
                )}
                <StaffForm
                  key={id}
                  type={id}
                  title={title}
                  singleTitle={singleTitle}
                  onAdd={handleAddStaff}
                  noStaffIsAdded={!(clinicStaff?.[id]?.length > 0 && !!clinicStaff?.[id])}
                />
              </>
            </AccordionDetails>
          </Accordion>
        )
      })} */
